<template>
  <el-dialog
    :visible.sync="visible"
    width="440px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    :append-to-body="true"
    custom-class="pending-bill-warning-dialog"
  >
    <div slot="title" class="flex items-center text-yellow-900">
      <i class="el-icon-warning warning-icon"></i>
      <span>Warning</span>
    </div>
    <div class="warning-body">
      <p class="warning-lead">Please process pending bill requests.</p>
      <p class="warning-count">
        There {{ pendingCount === 1 ? "is" : "are" }}
        <strong>{{ pendingCount }}</strong>
        pending bill request{{ pendingCount === 1 ? "" : "s" }}.
      </p>
    </div>
    <span slot="footer">
      <el-button type="warning" @click="acknowledge">Okay</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { Dialog, Button } from "element-ui";
import { mapActions } from "vuex";
import { authHelper, eventHelper } from "@/utils/helpers";

const IDLE_MS = 30000;
const SPEECH_REPEAT_MS = 8000;
const SPEECH_TEXT = "Please process pending bill requests";
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel"
];
const PUBLIC_PATHS = [
  "/securegate",
  "/register",
  "/forgotPassword",
  "/register-request"
];

export default {
  name: "PendingBillRequestIdleAlert",
  components: {
    [Dialog.name]: Dialog,
    [Button.name]: Button
  },
  data() {
    return {
      visible: false,
      pendingCount: 0,
      idleTimer: null,
      speechTimer: null,
      checking: false,
      audioCtx: null
    };
  },
  mounted() {
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, this.onActivity, { passive: true });
    });
    eventHelper.$on("userLoggedOut", this.onLoggedOut);
    this.resetIdleTimer();
  },
  beforeDestroy() {
    this.stopAlertSound();
    this.clearIdleTimer();
    ACTIVITY_EVENTS.forEach(event => {
      window.removeEventListener(event, this.onActivity);
    });
    eventHelper.$off("userLoggedOut", this.onLoggedOut);
  },
  methods: {
    ...mapActions({
      getPendingBillRequestCount: "billRequest/getPendingBillRequestCount"
    }),
    isPublicRoute() {
      const path = (this.$route && this.$route.path) || "";
      return PUBLIC_PATHS.some(
        publicPath => path === publicPath || path.startsWith(`${publicPath}/`)
      );
    },
    onActivity() {
      if (this.visible) return;
      this.resetIdleTimer();
    },
    clearIdleTimer() {
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
        this.idleTimer = null;
      }
    },
    resetIdleTimer() {
      this.clearIdleTimer();
      this.idleTimer = setTimeout(this.onIdle, IDLE_MS);
    },
    async onIdle() {
      if (this.visible || this.checking) return;
      if (!authHelper.check() || this.isPublicRoute()) {
        this.resetIdleTimer();
        return;
      }

      this.checking = true;
      try {
        const count = await this.getPendingBillRequestCount();
        if (count > 0) {
          this.pendingCount = count;
          this.visible = true;
          this.startAlertSound();
          return;
        }
      } catch (error) {
        // Stay quiet on network errors; try again after the next idle window.
      } finally {
        this.checking = false;
        if (!this.visible) this.resetIdleTimer();
      }
    },
    startAlertSound() {
      this.stopAlertSound();
      this.playAlert();
      this.speechTimer = setInterval(this.playAlert, SPEECH_REPEAT_MS);
    },
    stopAlertSound() {
      if (this.speechTimer) {
        clearInterval(this.speechTimer);
        this.speechTimer = null;
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    },
    playAlert() {
      this.playWarningBeep();
      this.speakWarning();
    },
    playWarningBeep() {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        if (!this.audioCtx) this.audioCtx = new AudioContext();
        if (this.audioCtx.state === "suspended") this.audioCtx.resume();

        const ctx = this.audioCtx;
        const now = ctx.currentTime;
        [880, 660, 880].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "square";
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.0001, now);
          const start = now + i * 0.18;
          gain.gain.exponentialRampToValueAtTime(0.08, start + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(start);
          osc.stop(start + 0.17);
        });
      } catch (e) {
        // Audio may be blocked until a user gesture; speech still tries.
      }
    },
    speakWarning() {
      if (!window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(SPEECH_TEXT);
      utterance.lang = "en-IN";
      utterance.rate = 0.95;
      utterance.volume = 1;
      const voices = window.speechSynthesis.getVoices() || [];
      const voice =
        voices.find(v => v.lang && v.lang.toLowerCase().startsWith("en")) ||
        null;
      if (voice) utterance.voice = voice;
      window.speechSynthesis.speak(utterance);
    },
    acknowledge() {
      this.stopAlertSound();
      this.visible = false;
      this.pendingCount = 0;
      this.resetIdleTimer();
    },
    onLoggedOut() {
      this.stopAlertSound();
      this.visible = false;
      this.pendingCount = 0;
      this.resetIdleTimer();
    }
  }
};
</script>

<style>
.pending-bill-warning-dialog {
  border-top: 6px solid #e6a23c;
}
.pending-bill-warning-dialog .el-dialog__header {
  background: #fdf6ec;
  padding-bottom: 12px;
}
.pending-bill-warning-dialog .warning-icon {
  margin-right: 8px;
  font-size: 22px;
  color: #e6a23c;
}
.pending-bill-warning-dialog .warning-lead {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #b88230;
}
.pending-bill-warning-dialog .warning-count {
  margin: 0;
  color: #606266;
}
</style>
